import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { dailyTasksService } from '@/features/daily-tasks/services/daily-tasks.service';
import { gamificationService } from '@/features/gamification/services/gamification.service';
import { storeService } from '@/features/store/services/store.service';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Store, CheckCircle2, XCircle, Target, Zap, ArrowUpRight, ArrowDownRight, Flame, ExternalLink, ShoppingBag, Users, Package } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useGlobalTaskModal } from '@/contexts/GlobalTaskModalContext';

export const Route = createFileRoute('/_authenticated/tasks-history')({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: typeof search.tab === 'string' ? search.tab : 'tasks',
  }),
  component: TasksHistoryPage,
});

function TasksHistoryPage() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const activeTab = tab;
  const setActiveTab = (value: string) => {
    navigate({ to: '/tasks-history', search: { tab: value } });
  };

  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [withPointsOnly, setWithPointsOnly] = useState(false);
  const [objectivesDialogOpen, setObjectivesDialogOpen] = useState(false);
  const [pointsPage, setPointsPage] = useState(1);
  const [purchasesPage, setPurchasesPage] = useState(1);
  const { openDailyTaskModal, openCalendarEventModal, openNotionTaskModal } =
    useGlobalTaskModal();

  const limit = 5;

  // Buscar perfil RPG para dashboard
  const { data: profile } = useQuery({
    queryKey: ['rpg-profile'],
    queryFn: () => gamificationService.getProfile(),
  });

  // Buscar progresso de nível para barra de XP
  const { data: progress } = useQuery({
    queryKey: ['rpg-profile-progress'],
    queryFn: () => gamificationService.getLevelProgress(),
  });

  // Buscar histórico de tarefas
  const { data: historyData, isLoading } = useQuery({
    queryKey: ['task-history', page, typeFilter, withPointsOnly],
    queryFn: () =>
      dailyTasksService.getTaskHistory({
        page,
        limit,
        type: typeFilter === 'all' ? undefined : typeFilter,
        withPointsOnly,
      }),
  });

  const tasks = historyData?.tasks || [];
  const pagination = historyData?.pagination || {
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0,
  };

  // Histórico de pontos/XP
  const { data: pointsHistoryData, isLoading: pointsLoading } = useQuery({
    queryKey: ['points-history', pointsPage],
    queryFn: () => storeService.getPointsHistory(pointsPage, 20),
    enabled: activeTab === 'points',
  });

  // Streak
  const { data: streak } = useQuery({
    queryKey: ['user-streak'],
    queryFn: storeService.getStreak,
  });

  const { data: purchasesData, isLoading: purchasesLoading } = useQuery({
    queryKey: ['store-my-purchases', purchasesPage],
    queryFn: () => storeService.getMyPurchases(purchasesPage, 20),
    enabled: activeTab === 'purchases',
  });

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      notion: 'Notion',
      manual: 'Manual',
      whatsapp: 'WhatsApp',
      personal: 'Pessoal',
      calendar: 'Calendário',
      playbook: 'Playbook',
    };
    return labels[type] || type;
  };

  const getTypeBadgeVariant = (type: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'outline'> = {
      notion: 'default',
      manual: 'secondary',
      whatsapp: 'outline',
      personal: 'secondary',
      calendar: 'secondary',
    };
    return variants[type] || 'default';
  };

  const handleTaskClick = (task: any) => {
    if (task.type === 'notion' && task.notionRefId && task.notionLink) {
      openNotionTaskModal(task);
    } else if (task.type === 'calendar' && task.calendarEventId) {
      openCalendarEventModal(task.calendarEventId);
    } else {
      openDailyTaskModal(task, 'admin-board');
    }
  };

  const isTaskClickable = (task: any) => {
    if (task.type === 'notion') {
      return !!task.notionRefId && !!task.notionLink;
    }
    if (task.type === 'calendar') {
      return !!task.calendarEventId;
    }
    return true;
  };

  return (
    <div className="space-y-6">
      {/* Dashboard Superior */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="relative">
                {profile?.avatarUrl ? (
                  <img
                    src={profile.avatarUrl}
                    alt="Avatar"
                    className="w-16 h-16 rounded-full object-cover border-2 border-border"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl border-2 border-border">
                    {profile?.class ? profile.class.charAt(0) : '?'}
                  </div>
                )}
                {profile?.level && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center border-2 border-background">
                    {profile.level}
                  </div>
                )}
              </div>

              {/* XP Info */}
              <div className="flex-1">
                <div className="text-sm text-muted-foreground">XP Atual</div>
                <div className="text-2xl font-bold">
                  {profile?.currentXp ? Number.parseFloat(profile.currentXp).toFixed(1) : '0.0'}
                </div>
                {profile?.level && (
                  <div className="text-xs text-muted-foreground">Nível {profile.level}</div>
                )}
                {progress && (
                  <div className="mt-2 space-y-1">
                    <Progress value={progress.progress} className="h-2" />
                    <div className="text-xs text-muted-foreground">
                      {Math.floor(progress.currentXp)}/{progress.xpForLevel} XP
                    </div>
                  </div>
                )}
              </div>

              {/* Pontos (saldo para gastar) */}
              <div className="flex-1 border-l border-border pl-4">
                <div className="text-sm text-muted-foreground">Pontos disponíveis</div>
                <div className="text-2xl font-bold">
                  {profile?.points != null
                    ? Number.parseFloat(profile.points).toFixed(1)
                    : '0.0'}
                </div>
                <div className="text-xs text-muted-foreground">
                  Para usar na loja de recompensas
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="flex flex-col gap-2 items-end">
              {streak && streak.currentStreak > 0 && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-md text-xs text-orange-700 dark:text-orange-300">
                  <Flame className="h-3 w-3 text-orange-500" />
                  <span className="font-semibold">{streak.currentStreak} dias OnFire</span>
                  {streak.currentStreak >= 5 && <span className="text-orange-500">• +0.1 XP/tarefa</span>}
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={() => setObjectivesDialogOpen(true)} variant="outline" size="sm">
                  <Target className="h-4 w-4 mr-2" />
                  Objetivos Diários
                </Button>
                <Button asChild variant="outline" size="sm">
                  <Link to="/store">
                    <Store className="h-4 w-4 mr-2" />
                    Loja de Pontos
                    <ExternalLink className="h-3 w-3 ml-1 opacity-50" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs principais */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="tasks">Histórico de Tarefas</TabsTrigger>
          <TabsTrigger value="points">
            <Zap className="h-3.5 w-3.5 mr-1.5" />
            Histórico de Pontos
          </TabsTrigger>
          <TabsTrigger value="purchases">
            <ShoppingBag className="h-3.5 w-3.5 mr-1.5" />
            Minhas Compras
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="mt-4 space-y-4">

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="space-y-2">
              <Label>Tipo de Tarefa</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="notion">Notion</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="personal">Pessoal</SelectItem>
                    <SelectItem value="calendar">Calendário</SelectItem>
                  </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2 pt-8">
              <Checkbox
                id="withPointsOnly"
                checked={withPointsOnly}
                onCheckedChange={(checked) => setWithPointsOnly(checked === true)}
              />
              <Label htmlFor="withPointsOnly" className="cursor-pointer">
                Apenas tarefas que geraram pontos
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Tarefas</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isLoading && tasks.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              Nenhuma tarefa concluída encontrada
            </div>
          )}
          {!isLoading && tasks.length > 0 && (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Título</TableHead>
                    <TableHead>Pontos Ganhos</TableHead>
                    <TableHead>Status XP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tasks.map((task) => {
                    const clickable = isTaskClickable(task);
                    return (
                      <TableRow
                        key={task.id}
                        onClick={() => handleTaskClick(task)}
                        className={cn(
                          clickable && 'cursor-pointer hover:bg-accent/50 transition-colors',
                        )}
                      >
                        <TableCell>
                          {task.completedAt
                            ? format(new Date(task.completedAt), "dd/MM/yyyy 'às' HH:mm", {
                                locale: ptBR,
                              })
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getTypeBadgeVariant(task.type)}>
                            {getTypeLabel(task.type)}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {task.title}
                            {clickable && (
                              <span className="text-xs text-primary font-medium">
                                {task.type === 'calendar'
                                  ? '(Clique para abrir evento)'
                                  : task.type === 'notion'
                                    ? '(Clique para abrir no Notion)'
                                    : '(Clique para abrir detalhes)'}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            if (task.xpCounted) {
                              const xpGained =
                                task.xpGained != null ? Number(task.xpGained) : undefined;
                              if (xpGained !== undefined) {
                                return (
                                  <span className="font-semibold text-green-600">
                                    +{xpGained.toFixed(1)} XP
                                  </span>
                                );
                              }
                            }
                            return <span className="text-muted-foreground">-</span>;
                          })()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {task.xpCounted ? (
                              <>
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                <span className="text-sm text-green-600">Pontos gerados</span>
                              </>
                            ) : (
                              <>
                                <XCircle className="h-4 w-4 text-red-500" />
                                <span className="text-sm text-red-500">
                                  {task.reachedDailyLimit
                                    ? 'Limite diário atingido antes desta tarefa'
                                    : 'Tarefa concluída sem geração de XP'}
                                </span>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Paginação */}
              {pagination.totalPages > 1 && (
                <div className="mt-4 flex justify-center">
                  <Pagination
                    total={pagination.total}
                    page={pagination.page}
                    limit={pagination.limit}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog Objetivos Diários */}
      <DailyObjectivesDialog open={objectivesDialogOpen} onOpenChange={setObjectivesDialogOpen} />

        </TabsContent>

        {/* Aba Histórico de Pontos */}
        <TabsContent value="points" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-yellow-500" />
                Histórico de Pontos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pointsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : !pointsHistoryData?.items?.length ? (
                <div className="text-center py-12 text-muted-foreground">
                  Nenhuma transação encontrada
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead>Motivo</TableHead>
                        <TableHead>Multiplicador</TableHead>
                        <TableHead className="text-right">Pontos</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pointsHistoryData.items.map((tx) => {
                        const amount = Number(tx.amount);
                        const isPositive = amount >= 0;
                        const reasonLabels: Record<string, string> = {
                          task_complete: 'Tarefa concluída',
                          task_revert: 'Estorno de tarefa',
                          playbook_step_complete: 'Etapa de playbook',
                          playbook_step_revert: 'Estorno de playbook',
                          store_purchase: 'Compra na loja',
                          store_group_contribution: 'Contribuição em grupo',
                          store_group_refund: 'Estorno de grupo',
                          streak_bonus: 'Bônus streak',
                          event_bonus: 'Bônus evento',
                          week_bonus: 'Bônus semana',
                          platform_contract_create: 'Contrato criado (D4Sign)',
                          platform_employee_contract_create: 'Contrato de funcionário criado',
                          platform_client_contract_create: 'Contrato de cliente criado',
                          platform_equipment_create: 'Equipamento criado',
                          platform_document_create: 'Documento criado',
                          platform_reimbursement_close: 'Pacote de reembolso fechado',
                          platform_client_create: 'Cliente criado',
                          platform_tech_system_create: 'Sistema técnico criado',
                          platform_daily_task_create: 'Tarefa criada',
                          platform_kanban_item_create: 'Item criado no kanban',
                          platform_pipeline_lead_create: 'Lead do pipeline criado',
                          platform_pipeline_asset_create: 'Asset de vendas criado',
                        };
                        return (
                          <TableRow key={tx.id}>
                            <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                              {format(new Date(tx.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                            </TableCell>
                            <TableCell className="text-sm max-w-xs truncate">
                              {tx.description || '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs whitespace-nowrap">
                                {reasonLabels[tx.reason] || tx.reason}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-center">
                              {Number(tx.multiplier) > 1 ? (
                                <Badge variant="secondary" className="text-xs">
                                  ×{tx.multiplier}
                                </Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className={cn('font-bold flex items-center justify-end gap-1', isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
                                {isPositive ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
                                {isPositive ? '+' : ''}{amount.toFixed(2)}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {pointsHistoryData.pagination.totalPages > 1 && (
                    <div className="mt-4 flex justify-center">
                      <Pagination
                        total={pointsHistoryData.pagination.total}
                        page={pointsHistoryData.pagination.page}
                        limit={pointsHistoryData.pagination.limit}
                        onPageChange={setPointsPage}
                      />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="purchases" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-emerald-500" />
                Minhas Compras
              </CardTitle>
            </CardHeader>
            <CardContent>
              {purchasesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : !purchasesData?.items?.length ? (
                <div className="text-center py-12 text-muted-foreground space-y-3">
                  <Package className="h-10 w-10 mx-auto opacity-40" />
                  <p>Nenhuma compra realizada ainda</p>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/store">Ir para a Loja de Pontos</Link>
                  </Button>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Produto</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead className="text-right">Pontos</TableHead>
                        <TableHead>Participantes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {purchasesData.items.map((purchase) => (
                        <TableRow key={purchase.id}>
                          <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                            {format(new Date(purchase.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {purchase.imageUrl ? (
                                <img
                                  src={purchase.imageUrl}
                                  alt={purchase.productName}
                                  className="w-10 h-10 rounded-md border object-cover shrink-0"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-md border bg-muted flex items-center justify-center shrink-0">
                                  <Package className="h-4 w-4 text-muted-foreground" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-sm">{purchase.productName}</p>
                                <p className="text-xs text-muted-foreground">{purchase.pointPrice} pts</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {purchase.purchaseType === 'group' ? (
                              <Badge variant="secondary" className="gap-1">
                                <Users className="h-3 w-3" />
                                Em grupo
                              </Badge>
                            ) : (
                              <Badge variant="outline">Individual</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="font-bold text-red-600 dark:text-red-400">
                              -{Number(purchase.pointsSpent).toFixed(1)}
                            </span>
                            {purchase.purchaseType === 'group' && purchase.userContribution && (
                              <p className="text-[11px] text-muted-foreground mt-0.5">
                                Sua parte: {Number(purchase.userContribution).toFixed(1)} pts
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            {purchase.purchaseType === 'group' && purchase.participants?.length ? (
                              <div className="space-y-1.5">
                                <Badge variant="outline" className="text-xs mb-1">
                                  {purchase.participantCount} participante{purchase.participantCount !== 1 ? 's' : ''}
                                </Badge>
                                <ul className="space-y-1">
                                  {purchase.participants.map((participant) => (
                                    <li
                                      key={participant.userId}
                                      className="text-xs text-muted-foreground flex items-center justify-between gap-2"
                                    >
                                      <span className="truncate max-w-[140px]">
                                        {participant.email ?? 'Usuário'}
                                      </span>
                                      <span className="font-medium text-foreground shrink-0">
                                        {Number(participant.points).toFixed(1)} pts
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {purchasesData.pagination.totalPages > 1 && (
                    <div className="mt-4 flex justify-center">
                      <Pagination
                        total={purchasesData.pagination.total}
                        page={purchasesData.pagination.page}
                        limit={purchasesData.pagination.limit}
                        onPageChange={setPurchasesPage}
                      />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

    </div>
  );
}

function DailyObjectivesDialog({
  open,
  onOpenChange,
}: Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}>) {
  const { data: objectivesData, isLoading } = useQuery({
    queryKey: ['daily-objectives'],
    queryFn: () => gamificationService.getDailyObjectives(),
    enabled: open,
  });

  const objectives = objectivesData?.objectives || [];

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      notion: 'bg-blue-500',
      whatsapp: 'bg-green-500',
      manual: 'bg-orange-500',
      personal: 'bg-pink-500',
      calendar: 'bg-cyan-500',
      playbook: 'bg-purple-500',
    };
    return colors[type] || 'bg-gray-500';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Objetivos Diários</DialogTitle>
          <DialogDescription>
            Acompanhe seu progresso nos limites diários de cada tipo de tarefa
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isLoading && objectives.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              Nenhum objetivo encontrado
            </div>
          )}
          {!isLoading && objectives.length > 0 && (
            <div className="space-y-4">
              {objectives.map((objective) => (
                <div key={objective.type} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${getTypeColor(objective.type)}`} />
                      <span className="font-medium">{objective.label}</span>
                      <Badge variant="secondary" className="text-xs">
                        {objective.points} XP por tarefa
                      </Badge>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {objective.current}/{objective.limit}
                    </span>
                  </div>
                  <Progress value={objective.progress} className="h-3" />
                  {objective.current >= objective.limit && (
                    <p className="text-xs text-green-600 font-medium">✓ Limite diário atingido!</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
